import { useMemo, useState } from 'react';
import { todayInTz, type TransactionDto } from '@trestly/shared';
import { useCategories, useSettings, useTransactions } from '../api/hooks';
import { usePeriod } from '../period-context';
import { ScreenHeader } from '../components/ScreenHeader';
import { TransactionSheet } from '../components/TransactionSheet';
import { useSheetState } from '../components/useSheetState';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { ListSkeleton } from '../components/Skeleton';
import { lookOf, NO_CATEGORY, useEnvelopeLook } from '../lib/envelope';
import { errorText } from '../lib/errors';
import { dayHeading } from '../lib/format';
import { money, moneyLabel } from '../lib/money';
import { periodLabel } from '../lib/period';
import { haptic } from '@/telegram';
import { t } from '../i18n';

/** Фильтр ленты: все, один конверт или траты без категории. */
type Filter = 'all' | 'none' | number;

/** Лента трат периода: дни группами, трата — одна строка с цветной точкой конверта. */
export function Feed(): JSX.Element {
  const { period } = usePeriod();
  const transactions = useTransactions(period);
  const categories = useCategories();
  const lookFor = useEnvelopeLook();
  const [filter, setFilter] = useState<Filter>('all');
  const editing = useSheetState<TransactionDto>();
  const todayIso = todayInTz();

  const visible = useMemo(
    () =>
      (transactions.data ?? []).filter((tx) =>
        filter === 'all' ? true : filter === 'none' ? tx.categoryId === null : tx.categoryId === filter,
      ),
    [transactions.data, filter],
  );

  const days = useMemo(() => {
    const groups = new Map<string, TransactionDto[]>();
    for (const tx of visible) {
      const list = groups.get(tx.spentOn) ?? [];
      list.push(tx);
      groups.set(tx.spentOn, list);
    }
    return [...groups.entries()].sort(([a], [b]) => (a < b ? 1 : -1));
  }, [visible]);

  const envelopes = [...(categories.data ?? [])].sort((a, b) => a.position - b.position);
  const total = visible.reduce((sum, tx) => sum + tx.amountBaseMinor, 0);
  const settings = useSettings();
  const baseCurrency = visible[0]?.baseCurrency ?? settings.data?.baseCurrency ?? 'BYN';

  return (
    <>
      <ScreenHeader
        title={t('feed.title')}
        meta={
          transactions.data
            ? `${t('feed.count', { n: visible.length })} · ${moneyLabel(total, baseCurrency)}`
            : null
        }
      />

      <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto px-4 py-1" role="group" aria-label={t('feed.envelopeAria')}>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          {t('feed.all')}
        </FilterChip>
        {envelopes.map((category) => (
          <FilterChip
            key={category.id}
            active={filter === category.id}
            color={lookOf(category).color}
            onClick={() => setFilter(category.id)}
          >
            {category.name}
          </FilterChip>
        ))}
        <FilterChip active={filter === 'none'} color={NO_CATEGORY.color} onClick={() => setFilter('none')}>
          {NO_CATEGORY.name}
        </FilterChip>
      </div>

      <div className="px-4">
        {transactions.isPending ? (
          <ListSkeleton rows={6} />
        ) : transactions.isError ? (
          <ErrorState message={errorText(transactions.error)} onRetry={() => void transactions.refetch()} />
        ) : days.length === 0 ? (
          <div className="panel">
            <EmptyState
              title={t('feed.emptyTitle')}
              description={filter === 'all' ? t('feed.emptyAll', { period: periodLabel(period) }) : t('feed.emptyEnvelope')}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {days.map(([day, list]) => (
              <section key={day}>
                <div className="flex items-baseline justify-between px-1 pb-1.5">
                  <h3 className="section-title">{dayHeading(day, todayIso)}</h3>
                  <span className="num text-[12.5px] text-faint">
                    {money(
                      list.reduce((sum, tx) => sum + tx.amountBaseMinor, 0),
                      list[0]?.baseCurrency ?? baseCurrency,
                    )}
                  </span>
                </div>
                <ul className="panel overflow-hidden" style={{ '--sep': '60px' } as React.CSSProperties}>
                  {list.map((tx) => {
                    const look = lookFor(tx.categoryId);
                    return (
                      <li key={tx.id}>
                        <button
                          type="button"
                          className="row-press flex min-h-[58px] w-full items-center gap-3 px-4 py-2 text-left"
                          style={{ '--c': look.color } as React.CSSProperties}
                          onClick={() => {
                            haptic.tap();
                            editing.show(tx);
                          }}
                        >
                          <span className="env-badge env-badge-sm" aria-hidden>
                            {look.name.trim().charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[15px] font-medium">{tx.note ?? look.name}</span>
                            {tx.note ? <span className="block truncate text-[12.5px] text-faint">{look.name}</span> : null}
                          </span>
                          <span className="text-right">
                            <span className="num block text-[15px] font-semibold">{moneyLabel(tx.amountMinor, tx.currency)}</span>
                            {tx.currency !== tx.baseCurrency ? (
                              <span className="num block text-[11.5px] text-faint">≈ {money(tx.amountBaseMinor, tx.baseCurrency)}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {editing.value ? (
        // key: лист правки заполняется из траты при монтировании — новая трата, новая форма.
        <TransactionSheet
          key={editing.value.id}
          open={editing.open}
          transaction={editing.value}
          categories={categories.data ?? []}
          onClose={editing.close}
        />
      ) : null}
    </>
  );
}

function FilterChip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      className="flex h-9 shrink-0 items-center gap-2 rounded-full bg-card px-3.5 text-[14px] font-medium whitespace-nowrap aria-pressed:bg-foreground aria-pressed:text-background"
      onClick={() => {
        haptic.select();
        onClick();
      }}
    >
      {color ? <span className="cat-dot" style={{ '--c': color } as React.CSSProperties} /> : null}
      {children}
    </button>
  );
}